/**
 * Scarab Cart Permalink - Emarsys Integration
 * Generates cart permalinks for abandoned cart recovery
 * Sends cart data to Emarsys via ScarabQueue custom tag
 */

(function() {
  'use strict';

  /**
   * Builds a Shopify cart permalink URL
   * @param {Object} cart - Shopify cart object
   * @param {Array} cart.items - Cart items array
   * @returns {string} Cart permalink URL
   */
  function buildCartPermalink(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return window.location.origin + '/cart';
    }
    
    var parts = cart.items.map(function(item) {
      return item.variant_id + ':' + item.quantity;
    });
    
    return window.location.origin + '/cart/' + parts.join(',');
  }

  /**
   * Formats the cart event date for the Emarsys custom tag.
   * @param {Date} date - Date in the visitor's local timezone
   * @returns {string} Date formatted as YYYY-MM-DD HH:mm:ss
   */
  function formatEmarsysDate(date) {
    return date.getFullYear() + '-'
      + String(date.getMonth() + 1).padStart(2, '0') + '-'
      + String(date.getDate()).padStart(2, '0') + ' '
      + String(date.getHours()).padStart(2, '0') + ':'
      + String(date.getMinutes()).padStart(2, '0') + ':'
      + String(date.getSeconds()).padStart(2, '0');
  }

  /**
   * Sends cart permalink to Emarsys via ScarabQueue
   * Fetches current cart state and pushes to ScarabQueue as custom tag
   */
  function sendCartLinkToEmarsys() {
    // Verify ScarabQueue is available
    if (typeof window.ScarabQueue === 'undefined') {
      console.warn('ScarabQueue not loaded, skipping cart permalink');
      return;
    }

    fetch('/cart.js')
      .then(function(response) { 
        return response.json(); 
      })
      .then(function(cart) {
        // Build permalink
        var permalink = buildCartPermalink(cart);
        var eventDate = formatEmarsysDate(new Date());
        
        // Build items list (comma-separated, max 255 chars)
        var itemsList = cart.items.map(function(item) {
          return item.variant_id + ':' + item.quantity;
        }).join(',');
        
        // Push to ScarabQueue as custom tag
        window.ScarabQueue.push([
          'tag',
          'cart_permalink',
          {
            cart_url: permalink,
            cart_items: itemsList,
            total_items: cart.item_count,
            date: eventDate
          }
        ]);
        
        // Execute queue
        window.ScarabQueue.push(['go']);
        
        // Push to dataLayer for GTM/debugging
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'cartPermalinkReady',
          cartPermalink: permalink,
          cartItemCount: cart.item_count
        });
      })
      .catch(function(error) {
        console.warn('Could not fetch /cart.js for Emarsys:', error);
      });
  }

  /**
   * Debounce function to limit execution frequency
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  function debounce(func, wait) {
    var timeout;
    return function() {
      var context = this;
      var args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() {
        func.apply(context, args);
      }, wait);
    };
  }

  // Debounced version (500ms) to avoid rapid successive calls
  var debouncedSendCartLink = debounce(sendCartLinkToEmarsys, 500);

  /**
   * Initialize event listeners
   */
  function init() {
    // Execute on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', sendCartLinkToEmarsys);
    } else {
      sendCartLinkToEmarsys();
    }

    // Listen for cart update events
    // Shopify themes typically dispatch these events
    var cartEvents = [
      'cart:update',
      'cart:add',
      'cart:change',
      'cart:remove',
      'cart:clear'
    ];

    cartEvents.forEach(function(eventName) {
      document.addEventListener(eventName, debouncedSendCartLink);
    });

    // Also listen for Shopify's native cart update event
    if (window.Shopify && window.Shopify.onCartUpdate) {
      var originalOnCartUpdate = window.Shopify.onCartUpdate;
      window.Shopify.onCartUpdate = function(cart) {
        originalOnCartUpdate.apply(window.Shopify, arguments);
        debouncedSendCartLink();
      };
    }
  }

  // Expose globally for debugging
  window.sendCartLinkToEmarsys = sendCartLinkToEmarsys;

  // Initialize
  init();

})();
